import { useEffect, useMemo, useState } from "react";
import {
  api,
  type LifecycleConfigDoc,
  type LifecycleConfigInput,
} from "../../lib/api";

interface FieldDef {
  key: keyof LifecycleConfigInput;
  label: string;
  hint: string;
  min: number;
  max: number;
}

const FIELDS: FieldDef[] = [
  {
    key: "artifact_ttl_days",
    label: "Retention period (days)",
    hint: "How long an artifact lives from its last update before it expires. Renewable via renew_artifact and auto-bumped when viewed near expiry.",
    min: 1,
    max: 730,
  },
  {
    key: "soft_delete_grace_days",
    label: "Soft-delete grace (days)",
    hint: "After it expires (or is deleted) an artifact is soft-deleted and stays recoverable for this many days before it is permanently purged.",
    min: 0,
    max: 365,
  },
  {
    key: "bump_on_view_window_days",
    label: "Auto-renew-on-view window (days)",
    hint: "Viewing an artifact within this many days of expiry resets its TTL — keeps actively-used artifacts alive. Cannot exceed the retention period.",
    min: 0,
    max: 730,
  },
];

function relativeTime(iso: string | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const seconds = (Date.now() - t) / 1000;
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function toInput(d: LifecycleConfigDoc): LifecycleConfigInput {
  return {
    artifact_ttl_days: d.artifact_ttl_days,
    soft_delete_grace_days: d.soft_delete_grace_days,
    bump_on_view_window_days: d.bump_on_view_window_days,
  };
}

/**
 * Admin → Lifecycle. Edit the three artifact-lifecycle knobs (retention TTL,
 * soft-delete grace, auto-renew-on-view window). Changes propagate within ~60s
 * (server-side cache TTL).
 *
 * Behaviour to surface to the admin: increasing the retention period
 * retroactively extends every existing artifact's expiry by the delta;
 * decreasing it applies only to new and renewed artifacts. The grace and
 * view-window knobs take effect on the next purge/view.
 */
export function LifecyclePage() {
  const [doc, setDoc] = useState<LifecycleConfigDoc | null>(null);
  const [draft, setDraft] = useState<LifecycleConfigInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "load" | "save">("load");

  useEffect(() => {
    setBusy("load");
    api.admin.lifecycle
      .get()
      .then((d) => {
        setDoc(d);
        setDraft(toInput(d));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(""));
  }, []);

  const dirty = useMemo(() => {
    if (!doc || !draft) return false;
    return FIELDS.some((f) => doc[f.key] !== draft[f.key]);
  }, [doc, draft]);

  // Mirror the backend validation so we never POST a NaN/out-of-bounds value.
  // An empty number input reads back as NaN, which JSON.stringify would send as
  // null — so block Save (and surface why) until every field is a valid integer.
  const validationError = useMemo<string | null>(() => {
    if (!draft) return null;
    for (const f of FIELDS) {
      const v = draft[f.key];
      if (!Number.isFinite(v)) return `${f.label} is required.`;
      if (!Number.isInteger(v)) return `${f.label} must be a whole number.`;
      if (v < f.min || v > f.max) {
        return `${f.label} must be between ${f.min} and ${f.max}.`;
      }
    }
    if (draft.bump_on_view_window_days > draft.artifact_ttl_days) {
      return "The auto-renew-on-view window can't exceed the retention period.";
    }
    return null;
  }, [draft]);

  function setField(key: keyof LifecycleConfigInput, value: number) {
    setNotice(null);
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  async function save() {
    if (!draft || validationError) return;
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      const updated = await api.admin.lifecycle.put(draft);
      setDoc(updated);
      setDraft(toInput(updated));
      if (updated.expiry_recomputed > 0) {
        const n = updated.expiry_recomputed;
        setNotice(
          `Saved. Retention increased — extended ${n} existing artifact${n === 1 ? "" : "s"}.`,
        );
      } else {
        setNotice("Saved.");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  if (busy === "load" && !doc) return <p className="text-slate-500">Loading…</p>;

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Lifecycle</h1>
      <p className="mb-1 text-sm text-slate-500">
        How long artifacts are retained before they expire and are purged. These
        values seed from environment defaults and can be tuned here without a
        redeploy. Changes propagate within ~60s.
      </p>
      <p className="mb-4 text-xs text-slate-400">
        Last updated {relativeTime(doc?.updated_at)}
        {doc?.updated_by ? ` by ${doc.updated_by}` : ""}.
      </p>

      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <strong>Increasing</strong> the retention period retroactively extends
        every existing artifact's expiry by the same amount.{" "}
        <strong>Decreasing</strong> it applies only to newly created or renewed
        artifacts — existing artifacts keep their current expiry and shorten
        naturally on their next edit or view. Nothing is ever expired early.
      </div>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
      {notice && (
        <p className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {notice}
        </p>
      )}

      {draft && (
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div
              key={f.key}
              className="rounded-lg border border-slate-200 bg-white p-4"
            >
              <label
                htmlFor={`lifecycle-${f.key}`}
                className="block text-sm font-semibold"
              >
                {f.label}
              </label>
              <p className="mb-2 mt-1 text-xs text-slate-500">{f.hint}</p>
              <input
                id={`lifecycle-${f.key}`}
                type="number"
                min={f.min}
                max={f.max}
                value={Number.isFinite(draft[f.key]) ? draft[f.key] : ""}
                onChange={(e) => setField(f.key, e.target.valueAsNumber)}
                className="w-32 rounded-md border border-slate-300 px-3 py-1.5 text-sm tabular-nums"
              />
              <span className="ml-2 text-xs text-slate-400">
                allowed {f.min}–{f.max}
              </span>
            </div>
          ))}

          {dirty && validationError && (
            <p className="text-right text-xs text-rose-600">{validationError}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={!dirty || busy === "save"}
              onClick={() => {
                if (!doc) return;
                setDraft(toInput(doc));
                setError(null);
                setNotice(null);
              }}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || !!validationError || busy === "save"}
              className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {busy === "save" ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
